/**
 * Банер «На пристрої Europe/Warsaw, у профілі Europe/Kyiv. Оновити профіль?» (PRF-04): пояс
 * пристрою інший, ніж чинний пояс людини. Мовчазного перемикання немає: у відрядженні нагадування
 * «стрибали» б. Вибір: «Оновити профіль» (`PATCH /me`), «Не зараз» (до наступного сеансу) і «Більше
 * не питати для цього пристрою» (`prefs.tzHintOff`, лише цей пристрій).
 *
 * Пояси порівнюємо за зсувом від UTC зараз, а не за назвою IANA: `Europe/Kyiv` і `Europe/Zaporozhye`
 * чи два міста з однаковим часом не розбіжність, людині нема що вирішувати. Показуємо людську назву
 * місця (`Варшава`), а не сиру назву IANA.
 *
 * Показуємо після того, як відповіли `/me` і профіль компанії: до того чинний пояс — лише кеш, і
 * порівнювати з ним рано. Тільки для тих, хто ввійшов.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { getFormatContext, subscribeFormatContext, zoneOffsetMinutes } from '../i18n/format.ts';
import { companyProfileQuery } from '../i18n/region.ts';
import { shell as t } from '../i18n/uk/shell.ts';
import { deviceZone } from '../i18n/zones.ts';
import { useFirstScreenDone } from '../perf/first-screen.ts';
import { saveMe } from '../platform/me-write.ts';
import { hasSessionHint } from '../platform/session.ts';
import { setPrefs, usePrefs } from '../prefs/prefs.ts';
import { Banner, GLOBAL_BANNER, toast } from '../ui/notify/index.ts';
import { meQuery } from './user.ts';

const now = () => Date.now();

type ZoneLabel = (zone: string) => string;

/**
 * Підписи поясів (`zone-names.ts`, десятки КБ) не входять у перший екран (#351): вантажимо чанк,
 * лише коли банер має показатися. Збій `import()` оболонку не ламає: `failed` показує банер із
 * сирою назвою IANA (див. `label`), а не стирає його. Банер чекає чанк (`ready`), щоб текст не
 * змінювався на очах.
 */
function useZoneLabel(needed: boolean) {
  const [fn, setFn] = useState<ZoneLabel | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!needed || fn || failed) return;
    let live = true;
    import('../i18n/zone-names.ts').then(
      (m) => live && setFn(() => m.zoneLabel),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [needed, fn, failed]);
  return { ready: fn !== null || failed, label: fn ?? ((zone: string) => zone) };
}

export function RegionBanner() {
  const queryClient = useQueryClient();
  const device = deviceZone();
  const signedIn = hasSessionHint();
  // `/me` і профіль компанії — після межі першого екрана, як у оболонці (i18n/region.ts).
  const firstScreen = useFirstScreenDone();
  const enabled = signedIn && firstScreen;
  const me = useQuery({ ...meQuery(), enabled });
  const company = useQuery({ ...companyProfileQuery(), enabled });
  const prefs = usePrefs();
  const ctx = useSyncExternalStore(subscribeFormatContext, getFormatContext, getFormatContext);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  const profile = ctx.timeZone;
  // Чинний пояс остаточний, коли відповіли `/me` і (якщо власного поясу немає) профіль компанії.
  const known = me.data !== undefined && (me.data.timeZone !== null || !company.isPending);
  const differs =
    device !== null &&
    device !== profile &&
    zoneOffsetMinutes(device, now()) !== zoneOffsetMinutes(profile, now());
  const wanted = signedIn && known && differs && !prefs.tzHintOff && !dismissed;
  const zones = useZoneLabel(wanted);
  const visible = wanted && zones.ready;

  const update = async () => {
    if (busy || !me.data || !device) return;
    setBusy(true);
    try {
      await saveMe(queryClient, me.data, { timeZone: device });
      toast({ text: t.regionSaved(zones.label(device)) });
    } catch {
      toast({ text: t.regionFailed, tone: 'alert' });
      setDismissed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Banner
      area="global"
      priority={GLOBAL_BANNER.region}
      when={visible}
      icon="calendar"
      className="banner--global"
      testId="banner-region"
      bannerKey="region-zone"
      actions={
        <>
          <button
            className="btn btn--sm btn--primary"
            type="button"
            aria-busy={busy || undefined}
            onClick={() => void update()}
          >
            {t.regionUpdate}
          </button>
          <button className="btn btn--sm" type="button" onClick={() => setDismissed(true)}>
            {t.regionLater}
          </button>
          <button
            className="btn btn--sm btn--ghost"
            type="button"
            onClick={() => setPrefs({ tzHintOff: true })}
          >
            {t.regionNever}
          </button>
        </>
      }
    >
      <span>{t.regionHint(device ? zones.label(device) : '', zones.label(profile))}</span>
    </Banner>
  );
}
